import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { settingsApi } from '../api/endpoints.js';
import { useAuth } from '../auth/AuthContext.jsx';

const BrandingContext = createContext(null);

/**
 * The company identity (name, logo, accent colour) shared by the whole app.
 *
 * It used to be fetched inside the sidebar, which meant that saving a new logo
 * in Settings changed nothing on screen until you reloaded the page - the
 * sidebar had already made its one request and had no reason to make another.
 * Holding it here gives Settings a `refresh()` to call after saving, so the
 * header updates the moment the logo does.
 *
 * IT IS FETCHED ON THE SIGNED-IN USER, not once at mount. This provider sits
 * above the login screen, so its first attempt happens while there is no token
 * yet: `/api/settings` needs one, the call 401s, and the branding stayed empty
 * for the whole session. The symptom was a logo that only appeared after a full
 * page reload - never straight after logging in. Keying the fetch on `user`
 * fixes that, and clears the branding on sign-out so the next person to log in
 * on this machine does not briefly see the previous company's name.
 */
export const BrandingProvider = ({ children }) => {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);

  const refresh = useCallback(
    () =>
      settingsApi
        .get()
        .then((settings) => {
          setCompany(settings.company ?? null);
          return settings.company ?? null;
        })
        .catch(() => null),
    [],
  );

  useEffect(() => {
    if (!user) {
      setCompany(null);
      return;
    }
    refresh();
  }, [user, refresh]);

  const value = useMemo(() => ({ company, refresh }), [company, refresh]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => useContext(BrandingContext) ?? { company: null, refresh: () => {} };
